import { Button, type ButtonSize } from '@heidi/design-system/button';
import { Textarea } from '@heidi/design-system/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP2Bold } from '@heidi/design-system/typography';
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from '@heidi/design-system/mobile-responsive-popover-drawer';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import type { LucideIcon } from 'lucide-react';
import { X } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { FeedbackType } from './customer-feedback';
import { feedbackPopoverMessages } from './FeedbackPopover.messages';
import { useOverlayTriggerTooltip } from '@heidi/utils';

interface FeedbackPopoverProps {
  feedbackType: FeedbackType;
  icon: LucideIcon;
  filledIcon: LucideIcon | React.ComponentType<{ size?: number; className?: string }>;
  isSelected: boolean;
  isOpen: boolean;
  isSubmitting: boolean;
  disabled: boolean;
  hasConversation: boolean;
  feedbackComment: string;
  isSubmitDisabled: boolean;
  buttonClassName: string;
  alignOffset: number;
  onOpenChange: (open: boolean) => void;
  onFeedbackClick: (feedback: FeedbackType) => void;
  onCancel: () => void;
  onSubmit: () => void;
  onCommentChange: (comment: string) => void;
  getModalTitle: () => string;
  getPlaceholderText: () => string;
  /**
   * Manual icon sizing, for consumers that leave `autoSizeSvg` off. Only `icon`
   * is read here — `button` is for the consumer's own `buttonClassName`.
   */
  sizeClasses?: {
    button: string;
    icon: string;
  };
  /** Button size variant. @default "md" */
  buttonSize?: ButtonSize;
  /** When true the design-system auto-sizes SVGs inside the button. @default false (disabled for backwards compat) */
  autoSizeSvg?: boolean;
  /** Which side the feedback popover opens on. @default "top" */
  popoverSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Test id for the trigger button, matching the rest of the action row. */
  'data-testid'?: string;
}

export const FeedbackPopover = ({
  feedbackType,
  icon: Icon,
  filledIcon: FilledIcon,
  isSelected,
  isOpen,
  isSubmitting,
  disabled,
  hasConversation,
  feedbackComment,
  isSubmitDisabled,
  buttonClassName,
  alignOffset,
  onOpenChange,
  onFeedbackClick,
  onCancel,
  onSubmit,
  onCommentChange,
  getModalTitle,
  getPlaceholderText,
  sizeClasses,
  buttonSize = 'md',
  autoSizeSvg = false,
  popoverSide = 'top',
  'data-testid': testId,
}: FeedbackPopoverProps) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();

  const getAriaLabel = () => {
    if (feedbackType === 'positive') {
      return intl.formatMessage(feedbackPopoverMessages.positiveLabel);
    } else if (feedbackType === 'negative') {
      return intl.formatMessage(feedbackPopoverMessages.negativeLabel);
    }
    return '';
  };

  const getTooltipText = () => {
    if (feedbackType === 'positive') {
      return intl.formatMessage(feedbackPopoverMessages.positiveTooltip);
    } else if (feedbackType === 'negative') {
      return intl.formatMessage(feedbackPopoverMessages.negativeTooltip);
    }
    return '';
  };

  return (
    <PopoverDrawer open={isOpen} onOpenChange={onOpenChange}>
      <TooltipProvider delayDuration={200}>
        <Tooltip {...tooltipProps}>
          <TooltipTrigger asChild>
            <PopoverDrawerTrigger
              asChild
              disabled={disabled || !hasConversation || isSubmitting}
            >
              <Button
                disableAutomaticSvgSizing={!autoSizeSvg}
                variant="ghost"
                size={buttonSize}
                iconOnly
                onClick={() => onFeedbackClick(feedbackType)}
                disabled={disabled || !hasConversation || isSubmitting}
                className={buttonClassName}
                aria-label={getAriaLabel()}
                data-testid={testId}
              >
                {isSelected ? (
                  <FilledIcon className={sizeClasses?.icon} />
                ) : (
                  <Icon className={sizeClasses?.icon} />
                )}
              </Button>
            </PopoverDrawerTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">{getTooltipText()}</TooltipContent>
        </Tooltip>
        <PopoverDrawerContent
          {...contentProps}
          className={cn(isMobile ? 'p-4' : 'w-80 p-3', 'z-[1000]')}
          hideCloseIcon={isMobile}
          align="start"
          side={popoverSide}
          sideOffset={popoverSide === 'top' ? -24 : 4}
          alignOffset={alignOffset}
        >
          {isMobile ? (
            <>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-medium leading-[21px]">{getModalTitle()}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  disableAutomaticSvgSizing
                  onClick={onCancel}
                  className="size-8 p-0"
                >
                  <X className="!size-[18px]" />
                </Button>
              </div>
              <div className="flex flex-col gap-2">
                <Textarea
                  value={feedbackComment}
                  onChange={(e) => onCommentChange(e.target.value)}
                  placeholder={getPlaceholderText()}
                  className="h-[156px] resize-none"
                  autoFocus
                />
                <div className="flex w-full gap-4">
                  <Button
                    disableAutomaticSvgSizing
                    variant="ghost"
                    size="md"
                    onClick={onCancel}
                    className="flex-1"
                  >
                    <FormattedMessage {...feedbackPopoverMessages.cancel} />
                  </Button>
                  <Button
                    disableAutomaticSvgSizing
                    size="md"
                    onClick={() => void onSubmit()}
                    disabled={isSubmitDisabled || isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? (
                      <FormattedMessage {...feedbackPopoverMessages.submitting} />
                    ) : (
                      <FormattedMessage {...feedbackPopoverMessages.submit} />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-2">
              <TypographyP2Bold className="text-sm">{getModalTitle()}</TypographyP2Bold>
              <Textarea
                value={feedbackComment}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder={getPlaceholderText()}
                className="h-24 resize-none"
                autoFocus
              />
              <div className="flex justify-end gap-2">
                <Button
                  disableAutomaticSvgSizing
                  variant="ghost"
                  size="xs"
                  onClick={onCancel}
                >
                  <FormattedMessage {...feedbackPopoverMessages.cancel} />
                </Button>
                <Button
                  disableAutomaticSvgSizing
                  size="xs"
                  onClick={() => void onSubmit()}
                  disabled={isSubmitDisabled || isSubmitting}
                >
                  {isSubmitting ? (
                    <FormattedMessage {...feedbackPopoverMessages.submitting} />
                  ) : (
                    <FormattedMessage {...feedbackPopoverMessages.submit} />
                  )}
                </Button>
              </div>
            </div>
          )}
        </PopoverDrawerContent>
      </TooltipProvider>
    </PopoverDrawer>
  );
};
